import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    Divider,
    Tooltip,
} from '@mui/material'
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/features/auth'
import { useLayoutStore } from '@/shared/store/layoutStore'
import { NAV_ITEMS, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './navItems'

const Sidebar = () => {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { user, logout, hasRole } = useAuthStore()
    const { sidebarCollapsed, toggleSidebar } = useLayoutStore()

    const width = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
    const navItems = NAV_ITEMS.filter((item) => !item.requiresRole || hasRole(item.requiresRole))

    return (
        <Drawer
            variant="permanent"
            sx={{
                width,
                flexShrink: 0,
                transition: 'width 0.2s ease',
                '& .MuiDrawer-paper': {
                    width,
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    transition: 'width 0.2s ease',
                    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: 'none',
                },
            }}
        >
            {/* Brand */}
            <Box sx={{ px: 2, height: 56, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {!sidebarCollapsed && (
                    <Typography
                        sx={{
                            fontFamily: 'Ubuntu',
                            fontWeight: 700,
                            fontSize: '13px',
                            letterSpacing: '4px',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        HOFFMANN GROUP
                    </Typography>
                )}
            </Box>

            <Divider />

            {/* Nav items */}
            <List sx={{ flexGrow: 1, px: 1, pt: 1 }}>
                {navItems.map((item) => {
                    const Icon = item.icon
                    const isActive = pathname === item.path
                    const label = t(item.labelKey)

                    const btn = (
                        <ListItemButton
                            onClick={() => navigate(item.path)}
                            selected={isActive}
                            sx={{
                                minHeight: 44,
                                borderRadius: 1,
                                justifyContent: sidebarCollapsed ? 'center' : 'initial',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                                    '&:hover': { bgcolor: 'primary.dark' },
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: sidebarCollapsed ? 0 : 1.5,
                                    justifyContent: 'center',
                                    color: 'inherit',
                                }}
                            >
                                <Icon size={18} />
                            </ListItemIcon>
                            {!sidebarCollapsed && (
                                <ListItemText
                                    primary={label}
                                    primaryTypographyProps={{ fontSize: '12px' }}
                                />
                            )}
                        </ListItemButton>
                    )

                    return sidebarCollapsed ? (
                        <Tooltip key={item.path} title={label} placement="right">
                            <span>{btn}</span>
                        </Tooltip>
                    ) : (
                        <span key={item.path}>{btn}</span>
                    )
                })}
            </List>

            <Divider />

            {/* User + collapse toggle */}
            <Box sx={{ p: 1 }}>
                <Tooltip
                    title={sidebarCollapsed ? `${user?.name || 'User'} — ${t('logout')}` : ''}
                    placement="right"
                >
                    <ListItemButton
                        onClick={logout}
                        sx={{
                            borderRadius: 1,
                            minHeight: 44,
                            justifyContent: sidebarCollapsed ? 'center' : 'initial',
                        }}
                    >
                        <ListItemIcon
                            sx={{ minWidth: 0, mr: sidebarCollapsed ? 0 : 1.5, justifyContent: 'center' }}
                        >
                            <LogOut size={18} />
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                            <ListItemText
                                primary={user?.name || user?.email || 'User'}
                                secondary={t('logout')}
                                primaryTypographyProps={{ fontSize: '12px', noWrap: true }}
                                secondaryTypographyProps={{ fontSize: '11px' }}
                            />
                        )}
                    </ListItemButton>
                </Tooltip>

                <ListItemButton
                    onClick={toggleSidebar}
                    sx={{
                        borderRadius: 1,
                        minHeight: 36,
                        justifyContent: sidebarCollapsed ? 'center' : 'initial',
                    }}
                >
                    <ListItemIcon
                        sx={{ minWidth: 0, mr: sidebarCollapsed ? 0 : 1.5, justifyContent: 'center' }}
                    >
                        {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                    </ListItemIcon>
                    {!sidebarCollapsed && (
                        <ListItemText
                            primary="Collapse"
                            primaryTypographyProps={{ fontSize: '11px' }}
                        />
                    )}
                </ListItemButton>
            </Box>
        </Drawer>
    )
}

export default Sidebar
